import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, UserPlus } from "lucide-react";
import { api, extractAxiosError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";
import { ROLE_LABEL } from "@/lib/permissions";
import { notifyInviteInboxChanged } from "@/lib/inviteEvents";

export function InviteWorkspaceMemberDialog({ orgId, open, onClose, onInvited }) {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("member");
    const [link, setLink] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) {
            setEmail("");
            setRole("member");
            setLink("");
            setBusy(false);
        }
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            const { data } = await api.post("/invitations", {
                email,
                role,
                org_id: orgId,
            });
            setLink(data.invite_link || "");
            toast.success("Workspace invitation sent", {
                description: `Email sent to ${email}. They'll create an account to join.`,
            });
            notifyInviteInboxChanged();
            onInvited?.(data.invitation);
        } catch (err) {
            toast.error(extractAxiosError(err, "Failed to send invitation"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            onClose={onClose}
            title={link ? "Invitation sent" : "Invite to workspace"}
            subtitle={
                link
                    ? "They can also use the link below. Track sent invites in Invite Center."
                    : "We'll email them a link to create a FairShare account and join your workspace."
            }
            testid="workspace-invite-dialog"
        >
            {!link ? (
                <form onSubmit={submit} className="space-y-3" data-testid="workspace-invite-form">
                    <div>
                        <label className="label-eyebrow">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                            placeholder="teammate@company.com"
                            data-testid="workspace-invite-email"
                        />
                    </div>
                    <div>
                        <label className="label-eyebrow">Workspace role</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                            data-testid="workspace-invite-role"
                        >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                        </select>
                        <p className="mt-1 text-[11px] text-white/40">
                            {role === "admin" ? "Can manage pools and invite others." : "Can view assigned pools and payouts."}
                        </p>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
                            data-testid="workspace-invite-submit"
                        >
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                            Send invitation
                        </button>
                    </DialogFooter>
                </form>
            ) : (
                <div className="space-y-3">
                    <p className="text-sm text-white/60">
                        Invitation queued for <span className="text-white font-medium">{email}</span> as{" "}
                        {ROLE_LABEL[role] || role}.
                    </p>
                    <div className="rounded-md border border-white/[0.08] bg-[#1A1A1A] p-3">
                        <p className="text-[11px] text-white/45 mb-1">Invite link</p>
                        <code className="text-xs font-mono text-white/75 break-all">{link}</code>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="btn-neon rounded-md px-4 py-2 text-sm">
                            Done
                        </button>
                    </DialogFooter>
                </div>
            )}
        </Dialog>
    );
}

export function WorkspaceInvitePanel({ activeOrg, onInvited }) {
    const [open, setOpen] = useState(false);

    if (!["owner", "admin"].includes(activeOrg?.role)) return null;

    return (
        <>
            <div className="surface p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="font-heading text-lg">Invite teammates</h3>
                        <p className="text-xs text-white/45 mt-1 max-w-lg">
                            Send a workspace invitation by email. New users create an account from the link; existing users
                            accept from{" "}
                            <Link to="/invites" className="text-[#00BF0D] hover:underline">
                                Invite Center
                            </Link>
                            .
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 shrink-0"
                        data-testid="workspace-invite-open"
                    >
                        <UserPlus className="h-4 w-4" />
                        Invite teammate
                    </button>
                </div>
            </div>
            <InviteWorkspaceMemberDialog
                orgId={activeOrg?.id}
                open={open}
                onClose={() => setOpen(false)}
                onInvited={() => {
                    setOpen(false);
                    onInvited?.();
                }}
            />
        </>
    );
}
